import React, {useEffect, useCallback, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {inject, observer} from "mobx-react";
import debounce from "lodash.debounce";
//local
import {Layout} from "components/UI/layout";
import {IconContract, IconUploadContract} from "components/icons";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {Preloader} from "components/Preloader";
import {DecorativeSearch} from "components/Filters/DecorativeSearch";
import {toUploadedContractId} from "constants/routes.const";
import {useNavigate} from "react-router-dom";
import EmptyData from "components/EmptyData";
import {PaginationType} from "components/Pagination";
import {color} from "constants/color.consts";
import {contractStatus, sortContractsSelectItems} from "constants/buyer.consts";
import {defaultUpdatedFieldSearch} from "constants/user.consts";
import {useSavedFilters} from "helpers/userHooks";
import {ContractsFilter, ContractsTableHead, ContractsTableItem} from "./components";

const statuses = Object.values(contractStatus);

const DEFAULT_FILTERS = {
  ...defaultUpdatedFieldSearch,
  search: undefined,
  status: [
    contractStatus.UPLOADED,
    contractStatus.RFT_COMPLETED,
    contractStatus.RFT_PENDING,
  ],
  projectId: null,
  page: 1,
};

const ContractsPage = inject("store")(
  observer(({store: {contractsStore}}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const searchInputRef = useRef("");
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [projects, setProjects] = useState(null);

    const [filters, setFilters] = useSavedFilters({
      key: "contractsPage",
      defaultValue: DEFAULT_FILTERS,
      allowedKeys: [
        "search",
        "status",
        "invoiceProfileIds",
        "field",
        "direction",
        "projectId",
        "page",
      ],
      allowedValues: {
        status: statuses,
        field: sortContractsSelectItems.map((item) => item.value.split("|")[0]),
      },
      listTypeKeys: ["status"],
      storage: "local",
    });

    const fetchContracts = useCallback(() => {
      contractsStore
        .getContracts(filters)
        .then(setData)
        .finally(() => setLoading(false));
    }, [contractsStore, filters]);

    const validateSearch = (search) =>
      search && search !== "undefined" && search !== "null" ? search : null;
    const handleSearch = debounce(() => {
      const search = searchInputRef.current?.value?.trim();
      const validatedSearch = validateSearch(search);
      if (validatedSearch) setFilters({...filters, search: validatedSearch, page: 1});
      else setFilters({...filters, search: null});
    }, 400);

    const handleChangeProject = (value) =>
      setFilters({...filters, page: 1, projectId: value});
    const handlePageChange = (value) => setFilters({...filters, page: `${value}`});

    useEffect(() => {
      if (filters) fetchContracts();
    }, [fetchContracts, filters]);

    useEffect(() => {
      contractsStore.getProjectsInContracts().then(setProjects);
    }, [contractsStore]);

    return (
      <Layout>
        <div className="our-contracts">
          <div className="content-block_header">
            {!!filters && (
              <DecorativeSearch
                icon={<IconContract width={40} height={36} />}
                name="OUR_CONTRACTS"
                defaultSearchValue={filters?.search}
                searchFocus={searchInputRef}
                searchOnChange={handleSearch}
                searchPlaceholder={t("FIND_SUPPLIERS_INPUT")}
                showSearch
                addClass="ma-0"
                showClearIcon
                onClearSearch={() => setFilters({...filters, search: undefined, page: 1})}
              />
            )}
            <ButtonTransparent
              name="UPLOAD"
              icon={<IconUploadContract color={color.blue} />}
              addClass="mla"
              small
              onClick={() => navigate(toUploadedContractId("create"))}
            />
          </div>
          {!!filters && <ContractsFilter filters={filters} setFilters={setFilters} />}

          {loading ? (
            <Preloader addClass="preloader500" />
          ) : (
            <div className="our-contracts content-block_wrapper">
              <ContractsTableHead
                projectId={filters?.projectId ?? null}
                projects={projects}
                onChangeProject={handleChangeProject}
              />
              {data?.contracts?.length ? (
                <div className="our-contracts_list">
                  {data?.contracts?.map((contract) => (
                    <ContractsTableItem
                      key={`${contract.type}-${contract.id}`}
                      contract={contract}
                    />
                  ))}
                </div>
              ) : (
                <EmptyData addClass="empty-data pt-4" />
              )}
              <PaginationType
                totalItems={data.totalItems}
                currentPage={data.currentPage}
                onPageChange={handlePageChange}
                addClass="mt-4 ml-2"
              />
            </div>
          )}
        </div>
      </Layout>
    );
  })
);

export default ContractsPage;
